// code to globally store ids , statuses and anything else required globally, subject to changes


import { createStore } from 'vuex';
import { fetch_form_metadata } from '@/services/form_metadata_service';
import {
  is_real_organization,
  SCOPED_CACHE_PREFIXES,
} from '@/utils/auth/session_constants.js';

const AUTH_SESSION_KEYS = {
  currentUserId: 'current_user_id',
  currentLeadId: 'current_lead_id',
  currentRole: 'current_role',
  currentLeadName: 'current_lead_name',
  organization: 'current_organization',
  isOrganizationAdmin: 'is_organization_admin',
  isPlatformAdmin: 'is_platform_admin',
};

function read_session_value(key, fallback = null) {
  if (typeof window === 'undefined') {
    return fallback;
  }

  const value = sessionStorage.getItem(key);
  return value ?? fallback;
}

function read_session_number(key) {
  const value = read_session_value(key);

  if (value === null || value === '') {
    return null;
  }

  const normalized_value = Number(value);
  return Number.isNaN(normalized_value) ? null : normalized_value;
}

function read_session_json(key, fallback = null) {
  const value = read_session_value(key);
  if (!value) {
    return fallback;
  }
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
}

function write_session_value(key, value) {
  if (typeof window === 'undefined') {
    return;
  }

  if (value === null || value === undefined || value === '') {
    sessionStorage.removeItem(key);
    return;
  }

  sessionStorage.setItem(key, String(value));
}

function read_session_bool(key) {
  return read_session_value(key) === 'true';
}

function normalize_bool(value) {
  return value === true || value === 'true';
}

function normalize_organization(organization) {
  if (!is_real_organization(organization)) {
    return null;
  }

  return organization;
}

function metadata_scope_key(state) {
  const organization_id = state.organization?.id || 'global';
  return `${state.currentUserId || 'anonymous'}:${organization_id}`;
}

function clear_auth_session() {
  if (typeof window === 'undefined') {
    return;
  }

  Object.values(AUTH_SESSION_KEYS).forEach((key) => {
    sessionStorage.removeItem(key);
  });

  Array.from({ length: localStorage.length }, (_, index) => localStorage.key(index))
    .filter((key) => key && SCOPED_CACHE_PREFIXES.some((prefix) => key.startsWith(prefix)))
    .forEach((key) => {
      localStorage.removeItem(key);
    });
}

const getInitialState = () => ({
  currentId: null,
  currentStatus: null,
  currentUserId: read_session_number(AUTH_SESSION_KEYS.currentUserId),
  currentLeadId: read_session_number(AUTH_SESSION_KEYS.currentLeadId),
  currentRole: read_session_value(AUTH_SESSION_KEYS.currentRole),
  currentLeadName: read_session_value(AUTH_SESSION_KEYS.currentLeadName),
  organization: normalize_organization(read_session_json(AUTH_SESSION_KEYS.organization)),
  isOrganizationAdmin: read_session_bool(AUTH_SESSION_KEYS.isOrganizationAdmin),
  isPlatformAdmin: read_session_bool(AUTH_SESSION_KEYS.isPlatformAdmin),
  currentFormId: null,
  entitiesById: {},
  questionTypes: [],
  formTypes: [],
  formStatuses: [],
  formMetadataLoading: false,
  formMetadataLoaded: false,
  formMetadataScopeKey: null,
});

const normalizeEntity = (payload = {}) => {
  const id = payload.id ?? payload.workflowId ?? payload.itemId ?? null;
  const status = payload.status ?? payload.workflowStatus ?? null;

  const result = {
    ...payload,
    id,
    status,
  };
  return result
};

const store = createStore({
  state: getInitialState,

  getters: {
    currentId: (state) => state.currentId,
    currentStatus: (state) => state.currentStatus,
    currentUserId: (state) => state.currentUserId,
    currentLeadId: (state) => state.currentLeadId,
    currentRole: (state) => state.currentRole,
    currentLeadName: (state) => state.currentLeadName,
    organization: (state) => state.organization,
    isOrganizationAdmin: (state) => state.isOrganizationAdmin,
    isPlatformAdmin: (state) => state.isPlatformAdmin,
    currentFormId: (state) => state.currentFormId,
    questionTypes: (state) => state.questionTypes,
    formTypes: (state) => state.formTypes,
    formStatuses: (state) => state.formStatuses,
    formMetadataLoading: (state) => state.formMetadataLoading,
    formMetadataLoaded: (state) => state.formMetadataLoaded,
    currentEntity: (state) => {
      if (!state.currentId) {
        return null;
      }

      return state.entitiesById[state.currentId] ?? null;
    },
    entityById: (state) => (id) => state.entitiesById[id] ?? null,
    statusById: (state) => (id) => state.entitiesById[id]?.status ?? null,
    hasStatus: (state) => (status) => state.currentStatus === status,
  },

  mutations: {
    SET_CURRENT_ID(state, id) {
      state.currentId = id;
    },
    SET_CURRENT_STATUS(state, status) {
      state.currentStatus = status;
    },
    SET_CURRENT_USER_ID(state, id) {
      if (String(state.currentUserId || '') !== String(id || '')) {
        state.formMetadataLoaded = false;
        state.formMetadataScopeKey = null;
        state.questionTypes = [];
        state.formTypes = [];
        state.formStatuses = [];
      }
      state.currentUserId = id;
      write_session_value(AUTH_SESSION_KEYS.currentUserId, id);
    },
    SET_CURRENT_LEAD_ID(state, id) {
      state.currentLeadId = id;
      write_session_value(AUTH_SESSION_KEYS.currentLeadId, id);
    },
    SET_CURRENT_ROLE(state, role) {
      state.currentRole = role;
      write_session_value(AUTH_SESSION_KEYS.currentRole, role);
    },
    SET_CURRENT_LEAD_NAME(state, leadName) {
      state.currentLeadName = leadName;
      write_session_value(AUTH_SESSION_KEYS.currentLeadName, leadName);
    },
    SET_ORGANIZATION(state, organization) {
      const normalized_organization = normalize_organization(organization);
      if ((state.organization?.id || null) !== (normalized_organization?.id || null)) {
        state.formMetadataLoaded = false;
        state.formMetadataScopeKey = null;
        state.questionTypes = [];
        state.formTypes = [];
        state.formStatuses = [];
      }
      state.organization = normalized_organization;
      write_session_value(
        AUTH_SESSION_KEYS.organization,
        normalized_organization ? JSON.stringify(normalized_organization) : null
      );
    },
    SET_IS_ORGANIZATION_ADMIN(state, isAdmin) {
      const normalized_is_admin = normalize_bool(isAdmin);
      state.isOrganizationAdmin = normalized_is_admin;
      write_session_value(AUTH_SESSION_KEYS.isOrganizationAdmin, normalized_is_admin ? 'true' : 'false');
    },
    SET_IS_PLATFORM_ADMIN(state, isAdmin) {
      const normalized_is_admin = normalize_bool(isAdmin);
      state.isPlatformAdmin = normalized_is_admin;
      write_session_value(AUTH_SESSION_KEYS.isPlatformAdmin, normalized_is_admin ? 'true' : 'false');
    },
    SET_CURRENT_FORM_ID(state, id) {
      state.currentFormId = id;
    },
    SET_FORM_METADATA_LOADING(state, isLoading) {
      state.formMetadataLoading = isLoading;
    },
    SET_FORM_METADATA_LOADED(state, isLoaded) {
      state.formMetadataLoaded = isLoaded;
      state.formMetadataScopeKey = isLoaded ? metadata_scope_key(state) : null;
    },
    SET_QUESTION_TYPES(state, questionTypes) {
      state.questionTypes = Array.isArray(questionTypes) ? questionTypes : [];
    },
    SET_FORM_TYPES(state, formTypes) {
      state.formTypes = Array.isArray(formTypes) ? formTypes : [];
    },
    SET_FORM_STATUSES(state, formStatuses) {
      state.formStatuses = Array.isArray(formStatuses) ? formStatuses : [];
    },
    UPSERT_ENTITY(state, payload) {
      const entity = normalizeEntity(payload);

      if (!entity.id) {
        return;
      }

      state.entitiesById = {
        ...state.entitiesById,
        [entity.id]: {
          ...(state.entitiesById[entity.id] ?? {}),
          ...entity,
        },
      };
    },
    SET_ACTIVE_ENTITY(state, payload) {
      const entity = normalizeEntity(payload);

      if (!entity.id) {
        return;
      }

      state.entitiesById = {
        ...state.entitiesById,
        [entity.id]: {
          ...(state.entitiesById[entity.id] ?? {}),
          ...entity,
        },
      };
      state.currentId = entity.id;
      state.currentStatus = entity.status;
    },
    CLEAR_ACTIVE_ENTITY(state) {
      state.currentId = null;
      state.currentStatus = null;
    },
    RESET_STORE(state) {
      clear_auth_session();
      Object.assign(state, getInitialState());
    },
  },

  actions: {
    setCurrentId({ commit }, id) {
      commit('SET_CURRENT_ID', id);
    },
    setCurrentStatus({ commit }, status) {
      commit('SET_CURRENT_STATUS', status);
    },
    setCurrentUserId({ commit }, id) {
      commit('SET_CURRENT_USER_ID', id);
    },
    setCurrentLeadId({ commit }, id) {
      commit('SET_CURRENT_LEAD_ID', id);
    },
    setCurrentRole({ commit }, role) {
      commit('SET_CURRENT_ROLE', role);
    },
    setCurrentLeadName({ commit }, leadName) {
      commit('SET_CURRENT_LEAD_NAME', leadName);
    },
    setOrganization({ commit }, organization) {
      commit('SET_ORGANIZATION', organization);
    },
    setIsOrganizationAdmin({ commit }, isAdmin) {
      commit('SET_IS_ORGANIZATION_ADMIN', isAdmin);
    },
    setIsPlatformAdmin({ commit }, isAdmin) {
      commit('SET_IS_PLATFORM_ADMIN', isAdmin);
    },
    setCurrentFormId({ commit }, id) {
      commit('SET_CURRENT_FORM_ID', id);
    },
    setQuestionTypes({ commit }, questionTypes) {
      commit('SET_QUESTION_TYPES', questionTypes);
    },
    setFormTypes({ commit }, formTypes) {
      commit('SET_FORM_TYPES', formTypes);
    },
    setFormStatuses({ commit }, formStatuses) {
      commit('SET_FORM_STATUSES', formStatuses);
    },
    upsertEntity({ commit }, payload) {
      commit('UPSERT_ENTITY', payload);
    },
    setActiveEntity({ commit }, payload) {
      commit('SET_ACTIVE_ENTITY', payload);
    },
    syncStatusFromBackend({ commit }, payload) {
      commit('SET_ACTIVE_ENTITY', payload);
    },
    clearActiveEntity({ commit }) {
      commit('CLEAR_ACTIVE_ENTITY');
    },
    async loadFormMetadata({ state, commit }, { force = false } = {}) {
      if (state.formMetadataLoading) {
        return;
      }

      if (state.formMetadataLoaded
        && state.formMetadataScopeKey === metadata_scope_key(state)
        && !force) {
        return;
      }

      commit('SET_FORM_METADATA_LOADING', true);

      try {
        const metadata = await fetch_form_metadata();

        commit('SET_QUESTION_TYPES', metadata?.question_types || []);
        commit('SET_FORM_TYPES', metadata?.form_types || []);
        commit('SET_FORM_STATUSES', metadata?.form_statuses || []);
        commit('SET_FORM_METADATA_LOADED', true);
      } finally {
        commit('SET_FORM_METADATA_LOADING', false);
      }
    },
    ensureFormMetadataLoaded({ state, dispatch }) {
      if (state.formMetadataLoaded
        && state.formMetadataScopeKey === metadata_scope_key(state)) {
        return Promise.resolve();
      }

      return dispatch('loadFormMetadata');
    },
    refreshFormMetadata({ dispatch }) {
      return dispatch('loadFormMetadata', { force: true });
    },
    resetStore({ commit }) {
      commit('RESET_STORE');
    },
  },
});

export default store;
